import { useState, useEffect, useRef } from 'react';
import {
    CELL_PADDING,
    HEADER_EXTRA,
    createTextMeasurer,
    isMeasurable,
} from './measureText';

// Columns whose widths are fixed by setColumnStyle; excluded from adaptive distribution
const FIXED_COLUMNS = new Set(['selection', 'expander', 'type', 'logo', 'action']);

// Approximate fixed widths in px (rem * 16) to subtract from available space
const FIXED_PX = { selection: 32, expander: 64, type: 64, logo: 48, action: 240 };

// Gutter left inside the container so the last column never touches the edge.
const CONTAINER_GUTTER = 16;

const shallowEqual = (a, b) => {
    const aKeys = Object.keys(a);
    if (aKeys.length !== Object.keys(b).length) return false;
    return aKeys.every((k) => a[k] === b[k]);
};

/**
 * Computes adaptive default column widths from the container size AND the
 * rendered content.
 *
 * PM-18150 AC: "columns automatically resize to fit content and screen size".
 * PM-18386 asks for measurable rules covering minimum widths, content-based vs
 * proportional sizing, and a tie-breaker for very wide content. The rules are:
 *
 *   1. natural width = max(header text, widest sampled cell) + padding
 *   2. clamped to [minColumnWidth, maxNaturalWidth] — the upper clamp is the
 *      tie-breaker that stops one long column swallowing the whole table
 *   3. if the naturals fit, the leftover space is shared out in proportion to
 *      natural width, so wide columns stay wide and narrow ones stay narrow
 *   4. if they do not fit, every column is scaled down by the same factor but
 *      never below minColumnWidth; the overflow shows as ellipsis + tooltip
 *
 * Below `collapseBelow` the table stops distributing widths altogether and
 * reports `isCollapsed`, so the caller can stack each row instead of scrolling
 * sideways (PM-18150 edge case, confirmed by Naveen 2026-09-11).
 */
export function useAdaptiveColumnLayout({
    containerRef,
    columns,
    data = [],
    minColumnWidth = 80,
    maxNaturalWidth = 360,
    collapseBelow = 0,
    sampleRows = 50,
}) {
    const [layout, setLayout] = useState({ columnWidths: {}, isCollapsed: false });
    const timerRef = useRef(null);
    const layoutRef = useRef(layout);
    layoutRef.current = layout;

    useEffect(() => {
        const el = containerRef.current;
        // Nothing to observe yet; the effect re-runs when a container appears.
        if (!el || !columns?.length) return undefined;

        const commit = (next) => {
            const {current} = layoutRef;
            if (
                current.isCollapsed === next.isCollapsed &&
                shallowEqual(current.columnWidths, next.columnWidths)
            ) {
                return; // nothing changed — avoid a pointless re-render
            }
            setLayout(next);
        };

        const compute = (containerWidth) => {
            if (!containerWidth) return;

            if (collapseBelow > 0 && containerWidth < collapseBelow) {
                // Stacked rows are full width; per-column widths do not apply.
                commit({ columnWidths: {}, isCollapsed: true });
                return;
            }

            const measure = createTextMeasurer();
            const rowsToSample = Math.min(data.length, sampleRows);
            let fixedTotal = 0;
            const flexColumns = [];

            columns.forEach((col) => {
                const id = col.accessor || col.id;
                if (FIXED_COLUMNS.has(id)) {
                    fixedTotal += FIXED_PX[id] || 64;
                    return;
                }

                const headerText = typeof col.Header === 'string' ? col.Header : id;
                let natural = measure(headerText) + HEADER_EXTRA;

                for (let i = 0; i < rowsToSample; i += 1) {
                    const value = data[i]?.[id];
                    if (isMeasurable(value)) {
                        natural = Math.max(natural, measure(value) + CELL_PADDING);
                    }
                }

                natural = Math.min(
                    Math.max(Math.ceil(natural), minColumnWidth),
                    maxNaturalWidth,
                );
                flexColumns.push({ id, natural });
            });

            if (flexColumns.length === 0) return;

            const available = Math.max(0, containerWidth - fixedTotal - CONTAINER_GUTTER);
            const naturalTotal = flexColumns.reduce((sum, c) => sum + c.natural, 0);
            const widths = {};

            if (naturalTotal <= available) {
                // Everything fits: share the surplus proportionally so the table
                // fills the container without horizontal scrolling.
                const surplus = available - naturalTotal;
                flexColumns.forEach((c) => {
                    widths[c.id] = Math.floor(c.natural + (surplus * c.natural) / naturalTotal);
                });
            } else {
                // Too narrow: shrink proportionally, holding the readability floor.
                const scale = available / naturalTotal;
                flexColumns.forEach((c) => {
                    widths[c.id] = Math.max(minColumnWidth, Math.floor(c.natural * scale));
                });
            }

            commit({ columnWidths: widths, isCollapsed: false });
        };

        const observer = new ResizeObserver((entries) => {
            clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => {
                compute(entries[0]?.contentRect.width);
            }, 200);
        });

        observer.observe(el);
        compute(el.offsetWidth);

        return () => {
            observer.disconnect();
            clearTimeout(timerRef.current);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [containerRef, columns, data, minColumnWidth, maxNaturalWidth, collapseBelow, sampleRows]);

    return layout;
}
